import { Fragment, ReactNode, MouseEvent, FormEvent, ChangeEvent } from 'react';
import { DatePicker, Select, Input, TimePicker, Radio } from 'antd';
import moment from 'moment';
import { AiOutlineEye } from 'react-icons/ai';

import cssStyles from './input.module.scss';
import { RadioChangeEvent } from 'antd/lib/radio';
import { FormInputElementConfigType } from '../../@types';

type PropsType = {
  showPeep?: boolean;
  disabled?: boolean;
  shouldValidate?: boolean;
  touched?: boolean;
  invalidCheck?: boolean;
  notVisible?: boolean;

  errorMessage?: string;
  elementType?:
    | 'input'
    | 'textarea'
    | 'date'
    | 'time'
    | 'radioGroup'
    | 'multiSelect'
    | 'select'
    | '';
  value?: string;
  classes?: string;
  label?: string;

  styles?: object;
  elementConfig: FormInputElementConfigType;

  optionValues?: Array<{ value: string; text: string }>;

  onIconClick?: () => void;
  onInputHover?: () => void;
  onInputClick?: () => void;
  onSelectClear?: () => void;
  onInputChanged?: (name: any, value: any) => void;

  labelIcon?: ReactNode;

  inputSize?: 'small' | 'middle' | 'large';
};

function AntdInput({
  showPeep,
  disabled,
  shouldValidate,
  touched,
  invalidCheck,
  notVisible,

  errorMessage,
  elementType,
  value,
  classes,
  label,

  styles,
  elementConfig,

  optionValues,

  onIconClick,
  onInputHover,
  onInputClick,
  onSelectClear,
  onInputChanged,

  labelIcon,

  inputSize,
}: PropsType) {
  let inputElement = null;
  let parentDivClass = `pb-3`;

  const showError = invalidCheck && shouldValidate && touched;
  if (showError) {
    parentDivClass = `${parentDivClass} ${cssStyles.inputErrorDiv}`;
  }

  function handleInputChange(event: FormEvent<HTMLInputElement>) {
    onInputChanged?.(event.currentTarget.name, event.currentTarget.value);
  }

  function handleSelectChange(val: any) {
    onInputChanged?.(elementConfig?.name, val);
  }

  function handleDateTimeChange(objValue: any, val: string) {
    onInputChanged?.(elementConfig?.name, val);
  }

  function handleRadioChange(event: RadioChangeEvent) {
    const name = event.target.name || elementConfig?.name;
    onInputChanged?.(name, event.target.value);
  }

  function handleTextAreaChange(event: ChangeEvent<HTMLTextAreaElement>) {
    onInputChanged?.(event.currentTarget.name, event.currentTarget.value);
  }

  switch (elementType) {
    case 'input':
      const antdInputProps: { suffix?: ReactNode } = {};

      if (showPeep) {
        const suffixProps: {
          onClick?: (event: MouseEvent) => void;
          onMouseEnter?: (event: MouseEvent) => void;
          onMouseLeave?: (event: MouseEvent) => void;
        } = {};
        if (onIconClick) {
          suffixProps.onClick = () => onIconClick();
        } else if (onInputHover) {
          suffixProps.onMouseEnter = () => onInputHover();
          suffixProps.onMouseLeave = () => onInputHover();
        }
        antdInputProps.suffix = (
          <Fragment>
            <AiOutlineEye {...suffixProps} style={{ cursor: 'pointer' }} />
          </Fragment>
        );
      }
      inputElement = (
        <Input
          {...elementConfig}
          {...antdInputProps}
          size={inputSize ? inputSize : 'large'}
          disabled={disabled || false}
          value={value}
          onChange={handleInputChange}
          onClick={onInputClick}
          className={classes}
          style={styles}
        />
      );
      break;

    case 'textarea':
      inputElement = (
        <Input.TextArea
          {...elementConfig}
          size={inputSize ? inputSize : 'large'}
          disabled={disabled || false}
          value={value}
          onChange={handleTextAreaChange}
          onClick={onInputClick}
          className={classes}
          style={styles}
        />
      );
      break;

    case 'date':
      const currentDate = moment(moment().toDate(), 'YYYY-MM-DD');
      inputElement = (
        <DatePicker
          {...elementConfig}
          disabled={disabled || false}
          className={`${disabled ? cssStyles.disabledInput : ''}`}
          size={inputSize ? inputSize : 'large'}
          style={{ width: '100%' }}
          value={
            value ? (moment(value, 'YYYY-MM-DD') as any) : (currentDate as any)
          }
          onChange={handleDateTimeChange}
        />
      );
      break;

    case 'time':
      const timeValue = value || '00:00:00';
      inputElement = (
        <TimePicker
          {...elementConfig}
          disabled={disabled || false}
          className={`${disabled ? cssStyles.disabledInput : ''}`}
          size={inputSize ? inputSize : 'large'}
          style={{ width: '100%' }}
          value={moment(timeValue, 'HH:mm:ss') as any}
          onChange={handleDateTimeChange}
        />
      );
      break;

    case 'radioGroup':
      inputElement = (
        <Radio.Group
          {...elementConfig}
          disabled={disabled || false}
          className={cssStyles.antdRadio}
          onChange={handleRadioChange}
          value={value}>
          {optionValues?.map((op) => (
            <Radio key={op.value} value={op.value}>
              {op.text}
            </Radio>
          ))}
        </Radio.Group>
      );
      break;

    case 'multiSelect':
      inputElement = (
        <Select
          disabled={disabled || false}
          mode="multiple"
          allowClear
          onClear={onSelectClear}
          value={value}
          size={inputSize ? inputSize : 'large'}
          style={{ width: '100%' }}
          placeholder="Please select"
          onChange={handleSelectChange}>
          {optionValues?.map((op, index) => (
            <Select.Option key={index} value={op.value}>
              {op.text}
            </Select.Option>
          ))}
        </Select>
      );
      break;

    case 'select':
      inputElement = (
        <Select
          {...elementConfig}
          disabled={disabled || false}
          className={`${disabled ? cssStyles.disabledInput : ''}`}
          value={value}
          defaultValue={value || ''}
          style={{ width: '100%' }}
          size={inputSize ? inputSize : 'large'}
          onChange={handleSelectChange}>
          {optionValues?.map((op, index) => (
            <Select.Option key={index} value={op.value}>
              {op.text}
            </Select.Option>
          ))}
        </Select>
      );
      break;

    default:
      inputElement = <Fragment></Fragment>;
  }

  return (
    <Fragment>
      {!notVisible && (
        <div className={`${parentDivClass}`}>
          {label && (
            <label htmlFor={elementConfig?.name} className={cssStyles.label}>
              {label !== 'nbsp' ? label : <span>&nbsp;</span>}
              {labelIcon && labelIcon}
            </label>
          )}
          {inputElement}
          {showError && (
            <div className={cssStyles.errorMsg}>{errorMessage}</div>
          )}
        </div>
      )}
    </Fragment>
  );
}

export default AntdInput;
